import { useState } from "react";
import { toast } from "react-toastify";
import { avancarOSApi } from "./service";
import { extrairNomeBase, VERIFICACOES_AVANCO } from "./regrasAvancoOS";

/**
 * Hook responsável pelo avanço de OS com validação de documentos.
 * Recebe do componente pai tudo que os modais de apoio precisam (setters de
 * modal, dados atuais, etc.) e devolve a função `avancarOS` pronta para
 * ligar ao botão "Avançar", além do estado do popup de alerta (`alertConfig`)
 * e do modal de confirmação (`alertConfirmAvancar`).
 */
export function useAvancarOS({
  dados,
  docsContexto,
  objDocs,
  numeroSolicitacao,
  clienteExists,
  fetchData,
  avancarDireto,
  setLoading,
  setLoadingImageProcess,
  setIsModalImportsAberturaAlteracaoEmpresa,
  setIsModalImportAlteracaoEnquadramento,
  setIsModalImportBaixaEncerramentoEmpresa,
  setModalUploadDoc,
  setNomeArquivoPadrao,
  setFileToRemove,
  setIsMostrarBtnConfirmacaoDeCasoSevero,
  setIsModalDataEncerramento,
  setIsModalAtualizarCliente,
  setOpcaoDadosEmpresa,
  setModoCadastro,
  setIsModalCadastrarCliente,
  setIsModalAtribuirContador,
  setIsModalDataMigracao,
  // Alteração (tipo 4) com mudança de UF/Município — ver
  // verificarAnaliseJuntaComercialAlteracao e verificarDocumentosCCM em
  // regrasAvancoOS.jsx. Vivem como estado no componente pai porque não há
  // campo persistido pra essa pergunta; duram só enquanto a tela estiver
  // aberta.
  mudancaUF,
  setMudancaUF,
  mudancaMunicipio,
  setMudancaMunicipio,
  enderecoAntesDaMudancaUF,
  setEnderecoAntesDaMudancaUF,
  imAntesDaMudanca,
  setImAntesDaMudanca,
  passouPorAJCM,
}) {
  const [alertConfig, setAlertConfig] = useState({ open: false, title: "", message: "" });
  const [alertConfirmAvancar, setAlertConfirmAvancar] = useState(false);

  const abrirModalImportacaoPorTipo = (tipoId) => {
    setAlertConfirmAvancar(false);
    if (["1", "4"].includes(tipoId)) setIsModalImportsAberturaAlteracaoEmpresa(true);
    if (tipoId === "3") setIsModalImportAlteracaoEnquadramento(true);
    if (tipoId === "5") setIsModalImportBaixaEncerramentoEmpresa(true);
  };

  const avancarOS = async () => {
    const safeDocsContexto = docsContexto || [];
    const requestNomeArquivo = (objDocs || []).map((filePath) => filePath.split(/[\\/]/).pop().toLowerCase());

    const ctx = {
      dados,
      docsContexto: safeDocsContexto,
      objDocs,
      requestNomeArquivo,
      clienteExists,
      fetchData,
      avancarDireto,
      setLoading,
      abrirModalImportacaoPorTipo,
      setAlertConfig,
      setAlertConfirmAvancar,
      setIsModalImportsAberturaAlteracaoEmpresa,
      setIsModalImportAlteracaoEnquadramento,
      setIsModalImportBaixaEncerramentoEmpresa,
      setModalUploadDoc,
      setNomeArquivoPadrao,
      setFileToRemove,
      setIsMostrarBtnConfirmacaoDeCasoSevero,
      setIsModalDataEncerramento,
      setIsModalAtualizarCliente,
      setOpcaoDadosEmpresa,
      setModoCadastro,
      setIsModalCadastrarCliente,
      setIsModalAtribuirContador,
      setIsModalDataMigracao,
      mudancaUF,
      setMudancaUF,
      mudancaMunicipio,
      setMudancaMunicipio,
      enderecoAntesDaMudancaUF,
      setEnderecoAntesDaMudancaUF,
      imAntesDaMudanca,
      setImAntesDaMudanca,
      passouPorAJCM,
      alteracaoContratualExiste: safeDocsContexto.some((arquivo) => extrairNomeBase(arquivo.nome).startsWith("Alteracao-contratual")),
      aberturaEmpresaArquivo: safeDocsContexto.find((arquivo) => extrairNomeBase(arquivo.nome).startsWith("Abertura-empresa")),
      EncerramentoEmpresaArquivo: safeDocsContexto.find((arquivo) => extrairNomeBase(arquivo.nome).startsWith("Modelo-Documento-distrato")),
      verificarArquivoObrigatorio: (fileNames, mensagemCustom) => {
        const carregados = safeDocsContexto.map((file) => file.nome.toLowerCase());
        const faltando = fileNames.find((file) => !carregados.some((c) => c.includes(file.toLowerCase())));
        if (faltando) {
          const mensagem = mensagemCustom[faltando] || "É necessário carregar os arquivos obrigatórios para prosseguir.";
          setAlertConfig({
            open: true,
            title: "Atenção",
            message: mensagem,
            onClose: () => setModalUploadDoc(true),
          });
          setAlertConfirmAvancar(false);
          return true;
        }
        return false;
      },
    };

    try {
      for (const verificar of VERIFICACOES_AVANCO) {
        // eslint-disable-next-line no-await-in-loop
        const barrou = await verificar(ctx);
        if (barrou) return;
      }

      setAlertConfirmAvancar(false);
      setLoading(true);
      setLoadingImageProcess(true);

      const response = await avancarOSApi(numeroSolicitacao);
      if (response.erro?.categoria?.id === "B") {
        toast.error(response.erro.mensagem);
        return;
      }
      if (response) {
        setAlertConfirmAvancar(false);
        await fetchData();
        setIsModalCadastrarCliente(false);
      } else {
        throw new Error("Nenhuma ação foi feita pela API");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
      setLoadingImageProcess(false);
    }
  };

  return { avancarOS, alertConfig, setAlertConfig, alertConfirmAvancar, setAlertConfirmAvancar, abrirModalImportacaoPorTipo };
}
